import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { ConorbitLoader } from "@/components/ui/ConorbitLoader";

const SENT_COLORS: Record<string, string> = {
  positive: "bg-emerald-100 text-emerald-700",
  neutral:  "bg-slate-100 text-slate-600",
  negative: "bg-red-100 text-red-700",
};

export function SentimentAnalytics() {
  const { orgId } = useOrgPermissions();

  const { data: topics = [], isLoading } = useQuery({
    queryKey: ["sentiment-topics", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from("listening_topics" as any)
        .select("*")
        .eq("org_id", orgId!)
        .not("sentiment", "is", null)
        .order("count", { ascending: false }) as any);
      return (data ?? []) as any[];
    },
  });

  const { positive, neutral, negative, total } = useMemo(() => {
    const pos = topics.filter((t: any) => t.sentiment === "positive").reduce((a: number, t: any) => a + (t.count ?? 0), 0);
    const neu = topics.filter((t: any) => t.sentiment === "neutral").reduce((a: number, t: any) => a + (t.count ?? 0), 0);
    const neg = topics.filter((t: any) => t.sentiment === "negative").reduce((a: number, t: any) => a + (t.count ?? 0), 0);
    const tot = pos + neu + neg;
    return { positive: pos, neutral: neu, negative: neg, total: tot };
  }, [topics]);

  const pct = (n: number) => total === 0 ? 0 : Math.round((n / total) * 100);

  if (isLoading) return <ConorbitLoader variant="inline" />;

  return (
    <div className="space-y-5">
      {/* Sentiment distribution */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Positive", value: positive, pct: pct(positive), cls: "text-emerald-600", Icon: TrendingUp },
          { label: "Neutral",  value: neutral,  pct: pct(neutral),  cls: "text-slate-500",  Icon: Minus },
          { label: "Negative", value: negative, pct: pct(negative), cls: "text-red-600",    Icon: TrendingDown },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <s.Icon className={`w-4 h-4 ${s.cls}`} />
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
              <div className={`text-2xl font-bold ${s.cls}`}>{s.pct}%</div>
              <div className="text-xs text-muted-foreground">{s.value} mentions</div>
              <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${s.label === "Positive" ? "bg-emerald-500" : s.label === "Negative" ? "bg-red-500" : "bg-slate-400"}`}
                  style={{ width: `${s.pct}%` }}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Topic breakdown */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Topics by Sentiment</CardTitle></CardHeader>
        <CardContent>
          {topics.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No topics with sentiment data. Add topics in the Listening tab.</p>
          ) : (
            <div className="space-y-2">
              {topics.map((topic: any) => (
                <div key={topic.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={`text-[10px] ${SENT_COLORS[topic.sentiment] ?? ""}`}>
                      {topic.sentiment}
                    </Badge>
                    <span className="text-sm font-medium truncate">{topic.topic}</span>
                    <span className="text-xs text-muted-foreground capitalize">({topic.topic_type})</span>
                  </div>
                  <span className="font-semibold text-sm shrink-0">{topic.count ?? 0}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
